/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, OnInit, ViewContainerRef, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SectionActionButtonComponent } from '@src/app/shared/section-action-button/section-action-button.component';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { ControlOn } from '@src/app/shared/form/app-form-control';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { ActionGridField, LinksTo } from '@src/app/shared/action/action-grid-field';
import { GridComponent } from '@src/app/shared/grid/grid.component';
import { Action } from '@src/app/model/action';
import { ActionListDialogComponent } from '@src/app/shared/action/action-list-dialog.component';
import { ActionType } from '@src/app/shared/action/add-action-form-action';

type SimpleFilter = 'all' | 'todo';

@Component({
    selector: 'app-action-grid',
    templateUrl: './action-grid.component.html',
    styleUrls: ['./action-grid.component.scss'],
    imports: [GridComponent, MatButtonModule, MatIconModule, MatTooltipModule, DatePipe,
        SectionActionButtonComponent]
})
export class ActionGridComponent implements OnInit, FormControlInterface {
    private cds = inject(ConfirmDialogService);
    private dialog = inject(MatDialog);

    @Input() control: GridControl;
    @Input() on: ControlOn = 'form';
    actionGridField: ActionGridField;

    /** Simple view only - which subset of the actions the table shows */
    simpleFilter: SimpleFilter = 'all';

    ngOnInit(): void {
        this.actionGridField.dialog = this.dialog;
        this.actionGridField.cds = this.cds;
    }

    private get actions(): Action[] {
        return this.actionGridField.actionList || [];
    }

    get total(): number { return this.actions.length; }
    get doneCount(): number { return this.actions.filter(a => a.completedAt).length; }
    get todoCount(): number { return this.total - this.doneCount; }

    /**
     * Not-completed first, soonest planned date first (what needs doing next); then completed,
     * most recently completed first. The Todo filter narrows to just the not-completed group.
     */
    get simpleActions(): Action[] {
        const todo = this.actions.filter(a => !a.completedAt)
            .sort((a, b) => (a.plannedDate < b.plannedDate ? -1 : 1));
        const done = this.actions.filter(a => a.completedAt)
            .sort((a, b) => (a.completedAt < b.completedAt ? 1 : -1));
        if (this.simpleFilter === 'todo') { return todo; }
        return [...todo, ...done];
    }

    /** An open action whose planned date has already passed */
    isOverdue(action: Action): boolean {
        return !action.completedAt && !!action.plannedDate
            && action.plannedDate < new Date().toISOString().substring(0, 10);
    }

    /** Row tooltip - the action's description as plain text (the field can hold rich text) */
    actionDescription(action: Action): string {
        const d = action.description;
        return d ? d.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '';
    }

    toggleFilter(filter: SimpleFilter): void {
        this.simpleFilter = this.simpleFilter === filter ? 'all' : filter;
    }

    /** The pop-up editor - the same dialog the full grid opens from its title button */
    openAction(action: Action): void {
        this.actionGridField.upsertAction(action).subscribe(updated => {
            if (updated && updated !== 1) {
                Object.assign(action, updated);
            }
        });
    }

    /** The expand action: show the same actions in the full grid, in a big dialog */
    openBigList(): void {
        this.dialog.open(ActionListDialogComponent, {
            data: { control: this.control },
            autoFocus: false,
            maxWidth: '95vw',
        });
    }

    markSimpleComplete(action: Action): void {
        this.actionGridField.completeAction(action, updated => Object.assign(action, updated));
    }

    addSimpleAction(actionType: ActionType = 'future'): void {
        this.actionGridField.upsertAction(null, actionType).subscribe(created => {
            if (created && created !== 1 && !this.actions.includes(created)) {
                this.actionGridField.actionList.push(created);
            }
        });
    }

    /** Full grid view - open the same dialog, then drop the saved action in as a new row. */
    addGridAction(actionType: ActionType): void {
        this.actionGridField.upsertAction(null, actionType).subscribe(created => {
            if (created && created !== 1) {
                this.control.addRow(created, true, true);
            }
        });
    }

    public static createComponent(vcr: ViewContainerRef, ctl: GridControl, on: ControlOn) {
        const componentRef = vcr.createComponent(ActionGridComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        componentRef.instance.actionGridField = ctl.field as ActionGridField;
        return componentRef;
    }

    public static make(linkedTo: LinksTo, options: Partial<ActionGridField> = {}) {

        const actionGridField: ActionGridField = new ActionGridField(options);
        actionGridField.formControlFactory = ActionGridComponent.createComponent;
        actionGridField.linksTo = linkedTo;

        return actionGridField;
    }
}
